import * as THREE from 'three';

import React, { useRef, useState } from 'react';

import { useFrame } from '@react-three/fiber';

type ClaudeLogoProps = {
  position: [number, number, number];
  scale?: number;
  rotation?: [number, number, number];
};

export function ClaudeLogo({
  position = [0, 0, 0],
  scale = 1,
  rotation = [0, 0, 0],
}: ClaudeLogoProps) {
  const logoRef = useRef<THREE.Group>(null);
  const [hover, setHover] = useState(false);

  // More accurate color from the image - coral/orange
  const CLAUDE_COLOR = '#F6815F';

  // Subtle animation
  useFrame((state) => {
    if (logoRef.current) {
      // Gentle rotation
      logoRef.current.rotation.z += 0.001;

      // Scale pulsing effect when hovered
      if (hover) {
        const pulseScale =
          1 + Math.sin(state.clock.getElapsedTime() * 2) * 0.03;
        logoRef.current.scale.set(pulseScale, pulseScale, 1);
      }
    }
  });

  return (
    <group
      position={position}
      rotation={rotation}
      scale={scale}
      onPointerOver={() => setHover(true)}
      onPointerOut={() => setHover(false)}
    >
      {/* Sunburst pattern */}
      <group ref={logoRef} position={[0, 0.03, 0]}>
        {/* Create more rays for a denser sunburst */}
        {Array.from({ length: 11 }).map((_, i) => {
          const angle = (i / 11) * Math.PI * 2;
          const length = 0.65; // All rays same length like in the image
          const width = 0.08; // Thinner rays

          return (
            <mesh
              key={i}
              position={[
                Math.cos(angle) * length * 0.4,
                Math.sin(angle) * length * 0.4,
                0,
              ]}
              rotation={[0, 0, angle]}
              castShadow
            >
              <boxGeometry args={[length, width, 0.02]} />
              <meshStandardMaterial
                color={CLAUDE_COLOR}
                emissive={CLAUDE_COLOR}
                emissiveIntensity={0.2}
                flatShading
              />
            </mesh>
          );
        })}

        {/* Add another set of rays offset slightly for the fuller sunburst look */}
        {Array.from({ length: 11 }).map((_, i) => {
          const angle = (i / 11) * Math.PI * 2 + Math.PI / 11; // offset
          const length = 0.55; // Slightly shorter for the secondary rays
          const width = 0.06; // Even thinner for the secondary rays

          return (
            <mesh
              key={`secondary-${i}`}
              position={[
                Math.cos(angle) * length * 0.4,
                Math.sin(angle) * length * 0.4,
                0,
              ]}
              rotation={[0, 0, angle]}
              castShadow
            >
              <boxGeometry args={[length, width, 0.01]} />
              <meshStandardMaterial
                color={CLAUDE_COLOR}
                emissive={CLAUDE_COLOR}
                emissiveIntensity={0.2}
                flatShading
              />
            </mesh>
          );
        })}

        {/* Center circle */}
        <mesh position={[0, 0, 0.01]} castShadow>
          <circleGeometry args={[0.15, 32]} />
          <meshStandardMaterial
            color={CLAUDE_COLOR}
            emissive={CLAUDE_COLOR}
            emissiveIntensity={0.3}
          />
        </mesh>
      </group>

      {/* Glow effect when hovered */}
      {hover && (
        <mesh position={[0, 0.01, -0.01]} receiveShadow>
          <circleGeometry args={[0.8, 32]} />
          <meshStandardMaterial
            color={CLAUDE_COLOR}
            transparent
            opacity={0.15}
          />
        </mesh>
      )}
    </group>
  );
}
